export enum TaskType {
  CUSTOMER_PICKUP = "CUSTOMER_PICKUP",
  CUSTOMER_DELIVERY = "CUSTOMER_DELIVERY",
  CUSTOMER_RETURN = "CUSTOMER_RETURN",
  CARRIER_DROPOFF = "CARRIER_DROPOFF",
  CARRIER_PICKUP = "CARRIER_PICKUP",
  DEPOT_RECEIVE = "DEPOT_RECEIVE",
  DEPOT_SORT = "DEPOT_SORT",
  DEPOT_DISPATCH = "DEPOT_DISPATCH",
  DEPOT_TRANSFER = "DEPOT_TRANSFER",
}

export enum TaskStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

export enum TripStatus {
  PLANNED = "PLANNED",
  ACTIVE = "ACTIVE",
  COMPLETED = "COMPLETED",
  ARCHIVED = "ARCHIVED",
}

export interface Address {
  id?: string;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  province?: string | null;
  postalCode?: string | null;
  country?: string | null;
  location?: unknown;
}

export interface Proof {
  id?: string;
  url?: string | null;
  type?: string | null;
  createdAt?: string | null;
}

export interface Shipment {
  id: string;
  shipmentNumber?: number;
  trackingNumber?: string | null;
  status?: string;
  cancellationRequestedAt?: string | null;
  order?: { id?: string; orderNumber?: number };
}

export interface Operator {
  id: string;
  status?: string;
  carrierUser?: {
    id?: string;
    email?: string | null;
    name?: string | null;
    phone?: string | null;
  } | null;
  lastLocation?: unknown;
  lastLocationTime?: string | null;
}

export interface Depot {
  id: string;
  name?: string | null;
  code?: string | null;
  address?: Address | null;
}

export interface Vehicle {
  id: string;
  code?: string | null;
  licensePlate?: string | null;
}

export interface Task {
  id: string;
  taskNumber: number;
  type: TaskType;
  status: TaskStatus;
  description?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt?: string;
  readyAt?: string | null;
  receivedAt?: string | null;
  scheduledWindow?: {
    slotId?: string;
    date: string;
    startTime: string;
    endTime: string;
  } | null;
  proof?: Proof | null;
  failureReason?: string | null;
  failureReasonCode?: number | null;
  shipmentId?: string | null;
  shipment?: Shipment | null;
  shipmentSnapshot?: {
    shipmentNumber?: number;
    orderNumber?: number;
    [key: string]: unknown;
  } | null;
  waypointId?: string | null;
  waypoint?: TripWaypoint | null;
  tripId?: string | null;
  trip?: Trip | null;
  addressId?: string | null;
  address?: Address | null;
  operatorId?: string | null;
  operator?: Operator | null;
  depotId?: string | null;
  depot?: Depot | null;
  parentTaskId?: string | null;
  parentTask?: Task | null;
  childTasks?: Task[];
  orderId?: string | null;
  order?: { id?: string; orderNumber?: number } | null;
}

export interface Trip {
  id: string;
  tripNumber: number;
  status: TripStatus;
  color?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  estimatedDuration?: number | null;
  actualDuration?: number | null;
  startLocation?: { lat: number; lng: number; address?: string };
  endLocation?: { lat: number; lng: number; address?: string };
  useCustomStartLocation?: boolean;
  useCustomEndLocation?: boolean;
  plannedRoute?: {
    routes?: Array<{
      polyline?: { encodedPolyline?: string };
    }>;
  } | null;
  actualRoute?: unknown;
  distance?: number | null;
  pendingTasks?: number;
  inProgressTasks?: number;
  cancelledTasks?: number;
  completedTasks?: number;
  failedTasks?: number;
  totalTasks?: number;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
  operatorId?: string | null;
  operator?: Operator | null;
  vehicleId?: string | null;
  vehicle?: Vehicle | null;
  depotId?: string | null;
  depot?: Depot | null;
  waypoints?: TripWaypoint[];
  tasks?: Task[];
}

export interface TripWaypoint {
  id: string;
  tripId: string;
  trip?: Trip;
  sequence: number;
  locationKey: string;
  location?: { lat: number; lng: number };
  radius?: number | null;
  tasks?: Task[];
  eta?: TripWaypointETA | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface TripWaypointETA {
  id?: string;
  waypointId?: string;
  estimatedArrivalTime?: string | null;
  estimatedDepartureTime?: string | null;
  estimatedDuration?: number | null;
  actualArrivalTime?: string | null;
  actualDepartureTime?: string | null;
  lastUpdated?: string | null;
}

export interface Waypoint {
  id: string;
  tripId: string;
  sequence: number;
  locationKey: string;
  location?: { lat: number; lng: number };
  radius?: number | null;
  tasks?: Task[];
  trip?: { color?: string | null };
  eta?: TripWaypointETA | null;
  createdAt?: string;
  updatedAt?: string;
}
